import React, { useCallback, useMemo, useState } from 'react';
import CompBasicTitleWithDesc from '@/basic/TitleWithDesc';
import '@/assets/styles/ui-component.less';
import type { IProps, IButtonItem, IPopupInfo  } from './IComponent';
import CommonBox from '@/basic/CommonBox/CommonBox';
import getGlobal from '@/utils/shared/getGlobal';
import { sendPageFbq } from '@/utils/shared/sendPageFbq';
import { sendGtag } from '@/utils/shared/sendGtag';
import { String } from '@reolink-web/utils';
import CompSubscribePopup from '@/basic/SubscribePopup';
import { ReoButton } from '@/deps/basic';
import { EMultiFunctionButtonType } from '@/interface';
import './index.less';

const FindBest: React.FC<IProps> = (props) => {

    const backgroundColor = useMemo(() => props.backgroundColor ? props.backgroundColor : '#000814', [props.backgroundColor]);
    const btnColor = useMemo(() => props.btnColor ? props.btnColor : '#ffffff', [props.btnColor]);

    const wrapStyle = React.useMemo(() => ({
        '--backgroundColor': backgroundColor,
        '--btnColor': btnColor,
        '--btnDefaultHoverColor': backgroundColor
    }) as React.CSSProperties, [backgroundColor, btnColor]);

    const mode = useMemo(() => props.mode === 'horizontal' ? 'horizontal' : 'vertical', [props.mode]);

    const [isShowPopup, setIsShowPopup] = useState(false);
    const [currentPopupInfo, setCurrentPopupInfo] = useState<IPopupInfo>({
        title: '',
        subject: '',
        gtagKey: '',
        sendToCodeGroup: [],
    });

    const btnClick = React.useCallback((item: IButtonItem) => {
        switch (item.type) {
            case EMultiFunctionButtonType.SUBSCRIBE_POPUP: {
                setCurrentPopupInfo({
                    title: item.popupTitle ?? '',
                    subject: item.subject ?? '',
                    gtagKey: item.gtagKey,
                    fbqKey: item.fbqKey,
                    sendToCodeGroup: item.sendToCodeGroup,
                });
                setIsShowPopup(true);
                break;
            }
            case EMultiFunctionButtonType.IMAGE:
            case EMultiFunctionButtonType.LINK:
            default:{
                if (!item?.url) break;
                location.href = item.url;
                break;
            }
        }
    }, []);

    const handleSubscribeSuccess = useCallback((): void => {
        if (currentPopupInfo.gtagKey) {
            getGlobal().gtag?.('event', currentPopupInfo.gtagKey, {
                'event_category': 'email-subscribe',
                'event_label': location.href,
            });
        }

        if (currentPopupInfo.fbqKey) {
            sendPageFbq('track', currentPopupInfo.fbqKey);
        }

        for (const item of currentPopupInfo.sendToCodeGroup ?? []) {
            sendGtag('conversion', {
                'send_to': item.sendToCode
            });
        }
    }, [currentPopupInfo]);

    return (
        <CommonBox style={ props.style }>
            <section
                style={ wrapStyle }
                className={ `find-best-title-wrap ${mode}` }
                id={ props.anchorKey ?? 'find-best' }
            >
                <CompBasicTitleWithDesc
                    title={ props.mainTitle }
                    desc={ props.description }
                    titleClassName={ 'find-best-main-title' }
                    descClassName={ 'find-best-desc' }
                    className='find-best-main-title-content'
                />
                <div className={ 'btn-group' }>
                    {
                        props.btnGroup?.map((btnItem) => {
                            if (btnItem.type === EMultiFunctionButtonType.IMAGE) {
                                return (
                                    <img
                                        className='find-best-image-button'
                                        key={ String.randomString(6) }
                                        src={ btnItem.imageUrl?.src }
                                        alt={ btnItem.imageUrl?.alt  }
                                        onClick={ btnClick.bind(null, btnItem) }
                                    />
                                );
                            }
                            return (
                                <ReoButton
                                    key={ String.randomString(6) }
                                    color={ btnColor }
                                    borderColor={ btnColor }
                                    backgroundColor={ 'transparent' }
                                    activeColor={ btnColor }
                                    activeBorderColor={ btnColor }
                                    activeBackgroundColor={ 'var(--white1)' }
                                    onClick={ btnClick.bind(null, btnItem) }
                                    className='find-best-button'
                                >
                                    { btnItem.name }
                                </ReoButton>
                            );
                        })
                    }
                </div>
                <CompSubscribePopup
                    open={ isShowPopup }
                    onClose={ () => {
                        setIsShowPopup(false);
                    } }
                    i18nPackage={ props.i18nPackage }
                    country={ props.country }
                    onSuccess={ handleSubscribeSuccess }
                    subjectKey={ currentPopupInfo.subject ?? '' }
                    title={ currentPopupInfo.title }
                    btnText={ currentPopupInfo.subscribeBtnText }
                />
            </section>
        </CommonBox>
    );
};

export default FindBest;
